<template lang="pug">
  v-form(ref="applicant-information" lazy-validation): v-row.px-5
    v-col(cols="6")
      label.primary--text 1. Akademischer Titel (optional)

      p.text-preview(v-if="readonly") {{ formData.titel }}
      v-text-field(
        v-else
        v-model="formData.titel"
        id="title"
        autocomplete="off"
        outlined
        dense
        @input="(v) => saveFieldValue('titel', v)"
      )

    v-col(cols="6")
      label.primary--text 2. Geschlecht

      p.text-preview(v-if="readonly") {{ formData.geschlecht }}
      v-select(
        v-else
        v-model="formData.geschlecht"
        id="geschlecht"
        outlined
        dense
        placeholder="Bitte Auswählen!"
        persistent-placeholder
        :items="genders"
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('geschlecht', v)"
      )

    v-col(cols="6")
      label.primary--text 3. Vorname(n) des Antragstellers* bzw. Unterzeichnungsbefugten*

      p.text-preview(v-if="readonly") {{ formData.vorname }}
      v-text-field(
        v-else
        v-model="formData.vorname"
        id="vorname"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/[^()\\d]+/g) ]"
        @input="(v) => saveFieldValue('vorname', v)"
      )

    v-col(cols="6")
      label.primary--text 4. Name(n) des Antragstellers* bzw. Unterzeichnungsbefugten*

      p.text-preview(v-if="readonly") {{ formData.name }}
      v-text-field(
        v-else
        v-model="formData.name"
        id="name"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/[^()\\d]+/) ]"
        @input="(v) => saveFieldValue('name', v)"
      )

    v-col(cols="6")
      label.primary--text 5. Geburtsdatum

      p.text-preview(v-if="readonly") {{ formatDateTime(formData.geburtstag, 'DD.MM.YYYY') }}
      n-date-picker(
        v-else
        v-model="formData.geburtstag"
        id="birthday"
        ref="birthday"
        :max-date="getDateByOffset(-18)"
        :min-date="getDateByOffset(-110)"
        :input-rules="[ rules.between_age(18, 110), rules.required, rules.valid_date('DD.MM.YYYY') ]"
        @input="(v) => saveFieldValue('geburtstag', v)"
      )

    v-col(cols="6")
      label.primary--text 6. Straße

      p.text-preview(v-if="readonly") {{ formData.strasse }}
      v-text-field(
        v-else
        v-model="formData.strasse"
        id="strasse"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/^(?:(?!\\s\\d+).)*$/) ]"
        @input="(v) => saveFieldValue('strasse', v)"
      )

    v-col(cols="6")
      label.primary--text 7. Hausnummer

      p.text-preview(v-if="readonly") {{ formData.hausnummer }}
      v-text-field(
        v-else
        v-model="formData.hausnummer"
        id="hausnummer"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/\\d+.*/) ]"
        @input="(v) => saveFieldValue('hausnummer', v)"
      )

    v-col(cols="6")
      label.primary--text 8. Postleitzahl

      p.text-preview(v-if="readonly") {{ formData.plz }}
      v-text-field(
        v-else
        v-model="formData.plz"
        id="plz"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/\\d{5}/) ]"
        @input="(v) => saveFieldValue('plz', v)"
      )

    v-col(cols="6")
      label.primary--text 9. Ort

      p.text-preview(v-if="readonly") {{ formData.ort }}
      v-text-field(
        v-else
        v-model="formData.ort"
        id="ort"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.valid_regexp(/[^\\d]+.*/) ]"
        @input="(v) => saveFieldValue('ort', v)"
      )

    v-col(cols="6")
      label.primary--text 10. Bundesland

      p.text-preview(v-if="readonly") {{ getStateById(formData.bundesland) }}
      v-select(
        v-else
        v-model="formData.bundesland"
        id="bundesland"
        outlined
        dense
        placeholder="Bitte Auswählen!"
        persistent-placeholder
        :items="states"
        :item-text="`text`"
        :item-value="`id`"
        :rules="[ rules.required ]"
        @input="(v) => saveFieldValue('bundesland', v)"
      )

    v-col(cols="6")
      label.primary--text 11. E-Mail-Adresse für die Antragskommunikation

      p.text-preview(v-if="readonly") {{ formData.email }}
      v-text-field(
        v-else
        v-model="formData.email"
        id="email"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.email ]"
        @input="(v) => saveFieldValue('email', v)"
      )

    v-col(v-if="!readonly" cols="6")
      label.primary--text 12. E-Mail-Adresse zur Prüfung bitte erneut eintragen
      v-text-field(
        v-model="formData.email_confirm"
        id="email_confirm"
        autocomplete="off"
        outlined
        dense
        :rules="[ rules.required, rules.email, rules.same_as(formData.email) ]"
        @input="(v) => saveFieldValue('email_confirm', v)"
        @paste="(e) => e.preventDefault()"
      )

    v-col(cols="6")
      label.primary--text 13. Telefon (Festnetz oder mobil)

      p.text-preview(v-if="readonly") {{ formData.telefon }}
      v-text-field.input-telephone(
        v-else
        v-model="formData.telefon"
        autocomplete="off"
        id="telefon"
        outlined
        dense
        persistent-placeholder
        :rules="[ rules.required, rules.valid_phone_number ]"
        @input="(v) => saveFieldValue('telefon', v)"
      )
        span(slot="prepend-inner") +49

    v-col(cols="6")
      label.primary--text 14. Name des Ensembles (optional)

      p.text-preview(v-if="readonly") {{ formData.name_ensembles }}
      v-text-field(
        v-else
        v-model="formData.name_ensembles"
        id="name_ensembles"
        autocomplete="off"
        outlined
        dense
        @input="(v) => saveFieldValue('name_ensembles', v)"
      )

    v-col(cols="6")
      label.primary--text 15. Künstler/in -Website (optional)

      p.text-preview(v-if="readonly") {{ formData.url }}
      v-text-field(
        v-else
        v-model="formData.url"
        id="url"
        autocomplete="off"
        outlined
        dense
        type="url"
        pattern="https://.*"
        persistent-placeholder
        :placeholder="!readonly ? `https://example.com` : ''"
        :rules="[ rules.valid_url ]"
        @input="(v) => saveFieldValue('url', v)"
      )

</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator'

import FormMixin from '@/mixins/form'

@Component({
  mixins: [
    FormMixin
  ],
})
export default class ApplicantInformation extends Vue {
  @Prop({ type: Boolean, default: false }) readonly

  get firstErrorField () {
    // console.log(this.$refs.birthday.inputField)
    const errorField = this.getFirstErrorField('applicant-information')
    const isDateValid = this.$refs.birthday.isValid()

    if (errorField) {
      if (errorField.id === 'geschlecht' || errorField.id === 'vorname' || errorField.id === 'name') {
        return errorField
      }
    }

    // check birthday field
    if (!isDateValid) {
      return this.$refs.birthday.inputField
    }

    return errorField
  }
}
</script>
